import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addMonths,
  format, isSameMonth, eachDayOfInterval, isBefore, startOfDay,
} from "date-fns";
import { fr } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

const STYLES = {
  available: "bg-emerald-500/15 text-emerald-700 border-emerald-500/40 hover:bg-emerald-500/30 cursor-pointer",
  full: "bg-red-500/15 text-red-600 border-red-500/30 cursor-not-allowed",
  closed: "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed",
};

export default function AvailabilityCalendar({ selected = [], onToggle, slug }) {
  const [cursor, setCursor] = useState(new Date());
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(true);

  const range = {
    start: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }),
  };

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const { data } = await api.get("/public/availability", {
        params: { slug, start: format(range.start, "yyyy-MM-dd"), end: format(range.end, "yyyy-MM-dd") },
      });
      const map = {};
      data.days.forEach((d) => { map[d.date] = d.status; });
      setDays(map);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, [cursor, slug]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const gridDays = eachDayOfInterval({ start: range.start, end: range.end });
  const weekDays = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  const today = startOfDay(new Date());

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="availability-calendar">
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={() => setCursor(addMonths(cursor, -1))} data-testid="avail-prev" className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
        <span className="font-heading font-semibold text-slate-900 capitalize">{format(cursor, "MMMM yyyy", { locale: fr })}</span>
        <button type="button" onClick={() => setCursor(addMonths(cursor, 1))} data-testid="avail-next" className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-100"><ChevronRight className="w-4 h-4" /></button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-emerald-600" /></div>
      ) : (
        <>
          <div className="grid grid-cols-7 mb-1">
            {weekDays.map((d) => <div key={d} className="text-center text-[10px] uppercase tracking-wider text-slate-500 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {gridDays.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const past = isBefore(day, today);
              let status = days[key] || "closed";
              if (past) status = "closed";
              const isSelected = selected.includes(key);
              const clickable = status === "available" && !past;
              return (
                <button
                  key={key}
                  type="button"
                  disabled={!clickable}
                  onClick={() => clickable && onToggle(key)}
                  data-testid={`avail-day-${key}`}
                  data-status={status}
                  className={`aspect-square rounded-lg border text-sm flex items-center justify-center transition ${STYLES[status]} ${!isSameMonth(day, cursor) ? "opacity-30" : ""} ${isSelected ? "ring-2 ring-emerald-400 !bg-emerald-500 !text-black font-bold" : ""}`}
                >
                  {format(day, "d")}
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className="flex flex-wrap gap-4 mt-4 pt-3 border-t border-slate-200 text-xs">
        <span className="flex items-center gap-1.5 text-slate-700"><span className="w-3 h-3 rounded bg-emerald-500/40 border border-emerald-500/60" /> Disponible</span>
        <span className="flex items-center gap-1.5 text-slate-700"><span className="w-3 h-3 rounded bg-red-500/40 border border-red-500/60" /> Complet</span>
        <span className="flex items-center gap-1.5 text-slate-700"><span className="w-3 h-3 rounded bg-white/10 border border-slate-300" /> Fermé</span>
      </div>
    </div>
  );
}
